/**
 * The consolidated counts endpoint reads straight from the picking database, so it
 * reports `order_details.status` - a different vocabulary from the delivery-order
 * statuses the rest of the admin (and the delivery-orders filter URLs) speak in.
 *
 * This module owns the translation in both directions:
 *   - picking status -> display status, for table columns and pipeline cards
 *   - display status -> DELIVERY_ORDER_STATUS_*, for the deep links into
 *     /operations/delivery-orders
 *
 * Anything the map does not know about is passed through untouched rather than
 * dropped, so a status added backend-side still shows up (and the column sums keep
 * matching the header total) until someone gets round to naming it here.
 */

const PICKING_TO_DISPLAY = {
  PENDING_PICKING: 'PENDING',
  PICKING: 'PROCESSING',
  PENDING_SHIPMENT: 'PACKED',
  SHIPPED: 'DISPATCHED',
  DELIVERED: 'DELIVERED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  RETURNED: 'RETURNED',
}

// Fulfilment order, left to right. DELIVERED and RETURNED have no column in the
// design but are kept so nothing silently disappears from the table.
const DISPLAY_ORDER = [
  'PENDING',
  'PROCESSING',
  'PACKED',
  'DISPATCHED',
  'DELIVERED',
  'COMPLETED',
  'CANCELLED',
  'RETURNED',
]

export const toDisplayStatus = (status) =>
  PICKING_TO_DISPLAY[status] || status || ''

/**
 * Collapses a list of picking statuses into display statuses, de-duplicated and
 * sorted into fulfilment order. Unknown statuses sort to the end, keeping their
 * relative order from the response.
 */
export const mapOrderStatuses = (statuses = []) => {
  const seen = []
  ;(statuses || []).forEach((status) => {
    const display = toDisplayStatus(status)
    if (display && seen.indexOf(display) === -1) {
      seen.push(display)
    }
  })

  return seen.sort((a, b) => {
    const aIndex = DISPLAY_ORDER.indexOf(a)
    const bIndex = DISPLAY_ORDER.indexOf(b)
    if (aIndex === -1 && bIndex === -1) {
      return 0
    }
    if (aIndex === -1) {
      return 1
    }
    if (bIndex === -1) {
      return -1
    }
    return aIndex - bIndex
  })
}

/**
 * Rewrites a per-slot count map keyed by picking status into one keyed by display
 * status, summing any statuses that collapse onto the same column.
 */
export const mapCountByDisplayStatus = (count = {}) =>
  Object.keys(count || {}).reduce((mapped, status) => {
    const display = toDisplayStatus(status)
    mapped[display] = (mapped[display] || 0) + (Number(count[status]) || 0)
    return mapped
  }, {})

/**
 * The delivery-orders listing filters on the DELIVERY_ORDER_STATUS_* enum, which the
 * display statuses map onto one-for-one.
 */
export const toDeliveryOrderStatus = (displayStatus) =>
  `DELIVERY_ORDER_STATUS_${String(displayStatus || '').toUpperCase()}`

const DISPLAY_TO_PICKING = Object.keys(PICKING_TO_DISPLAY).reduce(
  (reversed, picking) => {
    reversed[PICKING_TO_DISPLAY[picking]] = picking
    return reversed
  },
  {}
)

/**
 * The picking delivery-orders listing filters on order_details.status directly, so
 * links into it need the original picking status back. The map is one-for-one, so
 * this round-trips exactly.
 */
export const toPickingStatus = (displayStatus) =>
  DISPLAY_TO_PICKING[displayStatus] || displayStatus || ''
